import React, { useCallback, useEffect, useMemo, useState } from "react";
import MainLayout from "../../components/Layouts/Main/MainLayout.index";
import { getUserChangeHistory } from "../../api/project";
import "./UserChangeHistory.css";

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const FIELDS = [
  { value: "", label: "すべての項目" },
  { value: "auth_flg", label: "認証状態" },
  { value: "cancel_flg", label: "退会状態" },
  { value: "user_name", label: "ユーザー名" },
  { value: "group_name", label: "所属団体" },
];
const fieldLabel = (field) =>
  FIELDS.find((item) => item.value === field)?.label || field || "不明";
const dateString = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const initialFilters = () => {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - 29);
  return {
    userId: "",
    fieldName: "",
    from: dateString(from),
    to: dateString(to),
  };
};
const display = (value) =>
  value == null ? "NULL" : value === "" ? "（空文字）" : String(value);
const datetime = (value) => {
  if (!value) return "—";
  return String(value)
    .replace("T", " ")
    .replace(/\.\d{3,6}(?:Z|[+-]\d\d:\d\d)?$/, "")
    .slice(0, 19);
};
const makeCsvCell = (value) =>
  `"${String(value ?? "")
    .replace(/^[=+@\-\t\r]/, "'")
    .replace(/"/g, '""')}"`;
const getAuthLabel = (field, value) => {
  if (value == null) return "NULL";
  const s = String(value);
  if (field === "auth_flg")
    return (
      { 0: "未認証 (0)", 1: "認証済 (1)", 2: "審査中 (2)", 3: "拒否 (3)" }[s] ||
      s
    );
  if (field === "cancel_flg") return { 0: "在中 (0)", 1: "退会 (1)" }[s] || s;
  return display(value);
};
function normalizeResponse(json) {
  // Expected: { total, page, size, records: [...] }. Also tolerates Spring-style { content, totalElements }.
  const payload = json?.data && !Array.isArray(json.data) ? json.data : json;
  if (!payload || typeof payload !== "object")
    throw new Error("レスポンス形式が不正です。");
  const records = Array.isArray(payload.records)
    ? payload.records
    : payload.content;
  if (!Array.isArray(records)) throw new Error("records 配列がありません。");
  const total = Number(payload.total ?? payload.totalElements);
  if (!Number.isFinite(total) || total < 0)
    throw new Error("total が不正です。");
  return { records, total };
}

export default function UserChangeHistory() {
  const [editing, setEditing] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [result, setResult] = useState({ records: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const invalidRange = Boolean(
    editing.from && editing.to && editing.from > editing.to,
  );

  const load = useCallback(
    async (signal) => {
      setLoading(true);
      setError("");
      try {
        const json = await getUserChangeHistory(
          { ...filters, page, size },
          signal,
        );
        const data = normalizeResponse(json);
        if (!signal.aborted) setResult(data);
      } catch (err) {
        if (err.name !== "AbortError" && !signal.aborted) {
          setResult({ records: [], total: 0 });
          setError(`変更履歴の取得に失敗しました：${err.message}`);
        }
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [filters, page, size],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, reloadKey]);

  useEffect(() => {
    if (!selected) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setSelected(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selected]);

  const totalPages = Math.max(1, Math.ceil(result.total / size));
  const currentRows = useMemo(() => result.records, [result.records]);
  const apply = (event) => {
    event.preventDefault();
    if (invalidRange) return;
    setPage(1);
    setFilters({ ...editing, userId: editing.userId.trim() });
  };
  const reset = () => {
    const next = initialFilters();
    setEditing(next);
    setFilters(next);
    setPage(1);
  };
  const exportCurrentPage = () => {
    if (!currentRows.length) return;
    const header = [
      "変更日時",
      "ユーザーID",
      "変更項目",
      "変更前",
      "変更後",
      "履歴ID",
    ];
    const body = currentRows.map((row) => [
      datetime(row.changedAt),
      row.userId,
      fieldLabel(row.fieldName),
      row.oldValue,
      row.newValue,
      row.historyId,
    ]);
    const csv =
      "\uFEFF" +
      [header, ...body]
        .map((cols) => cols.map(makeCsvCell).join(","))
        .join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `user-change-history-page-${page}-${dateString(new Date())}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <MainLayout>
      <main className="uch-page">
        <header className="uch-heading">
          <div>
            <span className="uch-eyebrow">USER AUDIT LOG</span>
            <h1>ユーザー変更履歴</h1>
            <p>更新履歴を比較し、変更前・変更後の値を確認できます。</p>
          </div>
          <div className="uch-header-actions">
            <button
              type="button"
              className="uch-button uch-outline"
              onClick={() => setReloadKey((x) => x + 1)}
              disabled={loading}
            >
              再読み込み
            </button>
            <button
              type="button"
              className="uch-button uch-primary"
              onClick={exportCurrentPage}
              disabled={!currentRows.length || loading}
            >
              表示中のページをCSV出力
            </button>
          </div>
        </header>

        <section className="uch-panel" aria-label="検索条件">
          <form className="uch-filters" onSubmit={apply}>
            <label>
              ユーザーID
              <input
                value={editing.userId}
                onChange={(e) =>
                  setEditing((s) => ({ ...s, userId: e.target.value }))
                }
                placeholder="例: shoma23_mu"
                maxLength={100}
              />
            </label>
            <label>
              変更項目
              <select
                value={editing.fieldName}
                onChange={(e) =>
                  setEditing((s) => ({ ...s, fieldName: e.target.value }))
                }
              >
                {FIELDS.map((field) => (
                  <option key={field.value} value={field.value}>
                    {field.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              開始日
              <input
                type="date"
                value={editing.from}
                onChange={(e) =>
                  setEditing((s) => ({ ...s, from: e.target.value }))
                }
              />
            </label>
            <label>
              終了日
              <input
                type="date"
                value={editing.to}
                onChange={(e) =>
                  setEditing((s) => ({ ...s, to: e.target.value }))
                }
              />
            </label>
            <div className="uch-filter-actions">
              <button
                type="submit"
                className="uch-button uch-primary"
                disabled={invalidRange}
              >
                検索
              </button>
              <button
                type="button"
                className="uch-button uch-outline"
                onClick={reset}
              >
                クリア
              </button>
            </div>
          </form>
          {invalidRange && (
            <p className="uch-validation" role="alert">
              開始日は終了日以前を指定してください。
            </p>
          )}
        </section>

        <section className="uch-panel uch-results" aria-label="変更履歴一覧">
          <div className="uch-results-heading">
            <div>
              <h2>変更履歴一覧</h2>
              <p>
                <strong>{result.total.toLocaleString("ja-JP")}</strong> 件の変更
                {loading && (
                  <span className="uch-loading"> · 読み込み中...</span>
                )}
              </p>
            </div>
            <label className="uch-page-size">
              表示件数
              <select
                value={size}
                onChange={(e) => {
                  setSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}件
                  </option>
                ))}
              </select>
            </label>
          </div>
          {error && (
            <div className="uch-error" role="alert">
              {error}
            </div>
          )}
          <div className="uch-table-wrap">
            <table className="uch-table">
              <thead>
                <tr>
                  <th>変更日時</th>
                  <th>ユーザーID</th>
                  <th>変更項目</th>
                  <th>変更前</th>
                  <th></th>
                  <th>変更後</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {currentRows.map((row, index) => (
                  <tr key={`${row.historyId}-${row.fieldName}-${index}`}>
                    <td className="uch-mono uch-nowrap">
                      {datetime(row.changedAt)}
                    </td>
                    <td>
                      <strong className="uch-user">
                        {display(row.userId)}
                      </strong>
                    </td>
                    <td>
                      <span className="uch-field">
                        {fieldLabel(row.fieldName)}
                      </span>
                    </td>
                    <td>
                      <span
                        className="uch-value uch-old"
                        title={display(row.oldValue)}
                      >
                        {getAuthLabel(row.fieldName, row.oldValue)}
                      </span>
                    </td>
                    <td className="uch-arrow">→</td>
                    <td>
                      <span
                        className="uch-value uch-new"
                        title={display(row.newValue)}
                      >
                        {getAuthLabel(row.fieldName, row.newValue)}
                      </span>
                    </td>
                    <td>
                      <button
                        className="uch-detail-button"
                        type="button"
                        onClick={() => setSelected(row)}
                      >
                        詳細
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && !error && currentRows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="uch-empty">
                      該当する変更履歴はありません。
                    </td>
                  </tr>
                )}
                {loading && currentRows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="uch-empty">
                      読み込み中...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <footer className="uch-footer">
            <span>
              {result.total
                ? `${(page - 1) * size + 1}〜${Math.min(page * size, result.total)} / ${result.total} 件`
                : "0 件"}
            </span>
            <div className="uch-paging">
              <button
                type="button"
                onClick={() => setPage(1)}
                disabled={page <= 1 || loading}
                aria-label="先頭ページ"
              >
                «
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
              >
                前へ
              </button>
              <span>
                {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
              >
                次へ
              </button>
              <button
                type="button"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages || loading}
                aria-label="最終ページ"
              >
                »
              </button>
            </div>
          </footer>
        </section>
        <p className="uch-disclaimer">
          ※
          本画面の変更前の値は、保存済み履歴の直前のスナップショットとの比較です。履歴のない更新は復元できません。
        </p>
      </main>

      {selected && (
        <div
          className="uch-modal-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setSelected(null);
          }}
        >
          <section
            className="uch-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="uch-modal-title"
          >
            <div className="uch-modal-heading">
              <h2 id="uch-modal-title">変更詳細</h2>
              <button
                type="button"
                onClick={() => setSelected(null)}
                aria-label="閉じる"
                className="uch-close"
              >
                ×
              </button>
            </div>
            <dl className="uch-details">
              <dt>変更日時</dt>
              <dd>{datetime(selected.changedAt)}</dd>
              <dt>履歴ID</dt>
              <dd>{display(selected.historyId)}</dd>
              <dt>ユーザーID</dt>
              <dd>{display(selected.userId)}</dd>
              <dt>変更項目</dt>
              <dd>
                {fieldLabel(selected.fieldName)}{" "}
                <small>({selected.fieldName})</small>
              </dd>
              <dt>変更前</dt>
              <dd className="uch-pre">
                {getAuthLabel(selected.fieldName, selected.oldValue)}
              </dd>
              <dt>変更後</dt>
              <dd className="uch-pre">
                {getAuthLabel(selected.fieldName, selected.newValue)}
              </dd>
            </dl>
            <div className="uch-modal-footer">
              <button
                className="uch-button uch-primary"
                type="button"
                onClick={() => setSelected(null)}
              >
                閉じる
              </button>
            </div>
          </section>
        </div>
      )}
    </MainLayout>
  );
}
